/**
 * Футы в метры
 * ====================================
 * TODO Надо как-то обработать ошибку округления флоат
 */
import { Link, LoaderFunctionArgs, MetaDescriptor, MetaFunction, useLoaderData, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import H2 from "~/components/H2";
import AdditionalInfo from "~/components/tool-page/AdditionalInfo";
import AdditionalInfoSection from "~/components/tool-page/AdditionalInfoSection";
import Result from "~/components/tool-page/Result";
import ToolForm from "~/components/tool-page/ToolForm";
import { makeTitle } from "~/helpers/pageTitle";
import { createWebAppSchema, schemaOrg } from "~/helpers/schemaOrg";
import { getLocale } from "~/i18n";
import { getTranslator } from "~/i18next.server";
import LengthCalculator, { Unit } from "~/components/LengthCalculator";
import ToolHeaderNew from "~/components/tool-page/ToolHeaderNew";
import CalculatorLayout from "~/routes/layouts/components/CalculatorLayout";
import AdditionalContentLayout from "~/routes/layouts/components/AdditionalContentLayout";
import Author from "~/components/tool-page/Author";
import invariant from "tiny-invariant";
import { addNoindexMeta, ShouldIndexMeta } from "~/helpers/meta-noindex";

const CATEGORY = "length";
const TKEY = "calculators.length.feet_to_meters";
const ROUTE_ENDING = "feet-to-meters";
const LAST_UPDATED = new Date("2026-03-26T00:00:00.000Z");

export const meta: MetaFunction<typeof loader> = function({ data }) {
  invariant(data);

  const meta: MetaDescriptor[] = [
    { title: makeTitle(data?.meta.title) },
    { name: "description", content: data?.meta.description },
    createWebAppSchema(
      data?.meta.locale as string,
      data?.meta.title as string,
      [],
      LAST_UPDATED,
      schemaOrg.authorAlexFedorov
    )
  ];

  if (data.meta.canonical) {
    meta.push({ tagName: "link", rel: "canonical", href: data.meta.canonical });
  }

  addNoindexMeta(meta, data.meta.shouldIndex);

  return meta;
};


export const handle = {
  localisedLinks: ["en", "ru", "de", "fr", "es", "it", "pt"]
};

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
  const locale = getLocale(request.url) ?? "";
  const url = new URL(request.url);
  const number: number | null = params.number ? Number(params.number) : null;

  const t = await getTranslator(request);
``
  // Calculate result if number is set
  let error: string | null = null;
  let result: number | null = null;
  let notRoundedResult: number | null = null;
  let pluralizationCount = number ?? 0;

  if (number !== null) {
    if (Number.isNaN(number) || number < 0) {
      error = t(`${TKEY}.error_invalid_number`);
    } else {
      result = feetToCm(number, true);
      notRoundedResult = feetToCm(number, false);
    }

    if (number !== Math.floor(number)) {
      if (number < 2) {
        pluralizationCount = 2;
      } else {
        pluralizationCount = Math.floor(number);
      }
    }
  }

  const title = number
    ? t(`${TKEY}.title_for_number`, { count: pluralizationCount, number })
    : t(`${TKEY}.title`)
  ;

  const breadcrumbs = [
    { title: `catalog.categories.${CATEGORY}`, to: `${CATEGORY}` },
    { title: `${TKEY}.title`, to: `${CATEGORY}/${ROUTE_ENDING}` }
  ];

  if (number !== null) {
    breadcrumbs.push({ title, to: `${CATEGORY}/${ROUTE_ENDING}/${number}` });
  }

  const mainCalculatorURL = url.protocol + "//" + url.host + "/" + locale + "/" + CATEGORY + "/" + ROUTE_ENDING;

  let shouldIndex: ShouldIndexMeta = {
    googlebot: number === null,
    yandex: false,
    bingbot: false,
  };

  if (
    number === null
    || (number >= 0 && number <= 100 && number * 10 % 5 === 0)
  ) {
    shouldIndex.yandex = true;
    shouldIndex.bingbot = true;
  }

  return {
    number,
    result,
    notRoundedResult,
    error,
    meta: {
      locale,
      title,
      description: number !== null
        ? t(`${TKEY}.description_for_number`, { number, result: result })
        : t(`${TKEY}.description`),
      /**
       * Results page should have a canonical link to the main calculator page.
       * Attempt to glue links juice from results page to the main calculator page,
       * and avoid Google spam content filter
       */
      canonical: mainCalculatorURL,
      breadcrumbs,
      shouldIndex,
    }
  };
};

function feetToCm(number: number, round = true): number {
  const result: number = number * 0.3048;
  return round ? Math.round(result * 10000) / 10000 : result;
}

export default function Index() {
  const {
    meta,
    number,
    result,
    notRoundedResult,
    error
  } = useLoaderData<typeof loader>();

  const { t } = useTranslation("translations");
  const locale = getLocale(useLocation().pathname);

  const commonValues = [
    0.5,
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10,
    11,
    12,
    13,
    14,
    15,
    16,
    17,
    18,
    19,
    20,
    24,
    30,
    36,
    42,
    48,
    50,
    54,
    60,
    66,
    72,
    78,
    84,
    90,
    96,
    100
  ];

  return (<>
    <CalculatorLayout>
      <ToolHeaderNew
        name={meta.title}
        description={meta.description}
      />

      {result !== null && !error ? (
        <Result color="positive">
          <H2 className="first-letter:capitalize">{t("answer")}:</H2>

          <div className="mt-3 mb-8 text-center">
            {number}′ * 0.3048 = <strong className="mt-8 text-3xl first-letter:capitalize">
            {result} {t(`${TKEY}.unit`, { count: (result % 1 !== 0) ? 2 : result })}
          </strong>
          </div>
          {result !== notRoundedResult ? (
            <div className="mt-3 mb-8 text-center">
              {t(`${TKEY}.result_is_rounded`)} <br />
              {t(`${TKEY}.precise_value`, { result: notRoundedResult })}
            </div>
          ) : null}
        </Result>
      ) : null}

      <ToolForm>
        <LengthCalculator
          from={Unit.feet}
          to={Unit.meters}
          value={number ?? 1}
        />
      </ToolForm>

      {error ?
        <Result color="negative">
          <p>{error}</p>
        </Result>
        : null}
    </CalculatorLayout>

    <AdditionalContentLayout>
      <AdditionalInfo>
        <AdditionalInfoSection>
          <H2>{t(`${TKEY}.info_h_definition`)}</H2>
          <p>{t(`${TKEY}.info_p_definition`)}</p>
        </AdditionalInfoSection>

        <AdditionalInfoSection>
          <H2>{t(`${TKEY}.info_h_how_to_write`)}</H2>
          <p>{t(`${TKEY}.info_p_how_to_write`)}</p>
        </AdditionalInfoSection>

        <AdditionalInfoSection>
          <H2>{t(`${TKEY}.info_h_how_to_convert`)}</H2>
          <p>{t(`${TKEY}.info_p_how_to_convert`)}</p>
        </AdditionalInfoSection>

        <AdditionalInfoSection>
          <H2>{t(`${TKEY}.info_h_how_many_in_one_foot`)}</H2>
          <p>{t(`${TKEY}.info_p_how_many_in_one_foot`)}</p>
        </AdditionalInfoSection>

        {number !== null ? null : (
          <AdditionalInfoSection>
            <H2>{t(`${TKEY}.info_h_common_numbers`)}</H2>
            <p>{t(`${TKEY}.info_p_common_numbers`)}</p>
            <table className="w-full table table-fixed text-center border-collapse border border-gray-400 rounded-md">
              <thead>
              <tr>
                <th className="font-bold px-3 py-2 border-b border-r border-gray-400">{t(`${TKEY}.feet`)}</th>
                <th className="font-bold px-3 py-2 border-b border-gray-400">{t(`${TKEY}.unit_full`)}</th>
              </tr>
              </thead>
              <tbody>
              {commonValues.map((inches) => (
                <tr key={inches} className="hover:bg-slate-200 dark:hover:bg-slate-700">
                  <td className="px-3 py-2 border-b border-r border-gray-400">
                    {inches}
                  </td>
                  <td className="px-3 py-2 border-b border-gray-400">{feetToCm(inches)}</td>
                </tr>
              ))}
              </tbody>
            </table>
          </AdditionalInfoSection>
        )}
      </AdditionalInfo>

      <Author lastUpdated={LAST_UPDATED} />
    </AdditionalContentLayout>
  </>);
}


